import React, { useState, useEffect, useRef } from 'react';
import {
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  MenuItem,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  InputAdornment,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add,
  AccountBalance,
  ShoppingCart,
  Sell,
  MonetizationOn,
  Search,
  AccountBalanceWallet,
  Edit,
  CloudUpload,
  Delete,
} from '@mui/icons-material';
import { useAppContext } from '../context/AppContext';
import { useStockData } from '../context/StockDataContext';
import { useNotification } from '../context/NotificationContext';
import PageHeader from './common/PageHeader';
import { TransactionService } from '../services/transactionService';
import { StockNameManager } from '../services/stockNameManager';
import { HoldingsCalculationService } from '../services/holdingsCalculationService';
import { DecimalHelper } from '../utils/decimalHelper';
import { parseQuantity, parsePrice, validateTransactionPrice, maxPriceDecimals } from '../utils/formParsers';
import BatchImportForm, { BatchTransaction, csvTemplate } from './manualTrading/BatchImportForm';
import { parseCsvText, toLocalDateString } from '../utils/csvUtils';
import { logger } from '../utils/logger';

interface TradeForm {
  symbol: string;
  type: 'buy' | 'sell' | 'dividend' | 'cash_deposit' | 'cash_withdrawal' | 'stock_increase' | 'stock_decrease';
  quantity: string;
  price: string;
  date: string;
  adjustmentAmount?: string; // 資金調整金額（現金存入/提取時使用）
  transactionAdjustmentAmount?: string; // 交易調整總額
  transactionAdjustmentReason?: string; // 交易調整原因
}



const ManualTrading: React.FC = () => {
  const { state, addTransaction } = useAppContext();
  const { getDividendData } = useStockData();
  const { showSuccess, showError, showWarning } = useNotification();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentTab, setCurrentTab] = useState(0); // 0: 手動輸入, 1: 批次匯入
  const [form, setForm] = useState<TradeForm>({
    symbol: '',
    type: 'buy',
    quantity: '1000', // 預設股數 1000
    price: '',
    date: toLocalDateString(), // UI-06:本地時區的 yyyy-MM-dd
    adjustmentAmount: '',
    transactionAdjustmentAmount: '',
    transactionAdjustmentReason: '',
  });
  // FIN-07:提交中狀態(防重送)
  const [submitting, setSubmitting] = useState(false);
  const [stockSearching, setStockSearching] = useState(false);
  const [stockInfo, setStockInfo] = useState<{
    found: boolean;
    name?: string;
    currentPrice?: number;
  }>({ found: false });
  const [batchTransactions, setBatchTransactions] = useState<BatchTransaction[]>([]);
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [importResult, setImportResult] = useState<{
    success: number;
    failed: number;
    errors: string[];
  } | null>(null);

  // 自動查詢股價功能
  const searchStockPrice = async (symbol: string) => {
    if (!symbol.trim()) {
      setStockInfo({ found: false });
      return;
    }

    setStockSearching(true);
    
    try {
      // 優先從快取的股票數據中查找
      const cachedStock = state.stocks.find(s => 
        s.symbol.toUpperCase() === symbol.toUpperCase()
      );
      
      if (cachedStock) {
        setStockInfo({
          found: true,
          name: cachedStock.name,
          currentPrice: cachedStock.currentPrice
        });
        
        // 如果找到股票且價格欄位為空，自動填入當前股價
        if (!form.price) {
          setForm(prev => ({
            ...prev,
            price: cachedStock.currentPrice.toString()
          }));
        }
        return;
      }
      
      // 如果快取中沒有，直接使用 StockService 查詢（會自動使用真實 API）
      
      // 動態引入 StockService 以避免循環依賴
      const { StockService } = await import('../services/stockService');
      const stock = await StockService.getStockBySymbol(symbol);
      
      if (stock) {
        setStockInfo({
          found: true,
          name: stock.name,
          currentPrice: stock.currentPrice
        });
        
        // 如果找到股票且價格欄位為空，自動填入當前股價
        if (!form.price) {
          setForm(prev => ({
            ...prev,
            price: stock.currentPrice.toString()
          }));
        }
      } else {
        setStockInfo({ found: false });
      }
    } catch (error) {
      console.error('❌ 查詢股價失敗:', error);
      setStockInfo({ found: false });
    } finally {
      setStockSearching(false);
    }
  };

  // 監聽股票代號變化，自動查詢股價
  useEffect(() => {
    const timer = setTimeout(() => {
      if (form.symbol && form.symbol.length >= 4) {
        searchStockPrice(form.symbol);
      } else {
        setStockInfo({ found: false });
      }
    }, 500); // 延遲 500ms 避免過於頻繁的查詢

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.symbol, state.stocks]); // 加入 state.stocks 依賴，忽略 searchStockPrice 依賴以避免無限循環

  const handleInputChange = async (field: keyof TradeForm, value: string) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
    
    // 當交易類型切換為股利時，自動獲取並填入目前持有股數與最新股利金額
    if (field === 'type' && value === 'dividend' && form.symbol) {
      try {
        // 1) 自動帶入目前持有股數
        const holding = HoldingsCalculationService.calculateSingleStockHolding(
          form.symbol.toUpperCase(),
          state.transactions,
          state.stocks.find(s => s.symbol.toUpperCase() === form.symbol.toUpperCase()),
          { includeFees: true }
        );
        if (holding && holding.currentQuantity > 0) {
          setForm(prev => ({
            ...prev,
            quantity: holding.currentQuantity.toString()
          }));
        }

        // 2) 自動帶入最接近現在且已發放的股利(金額與日期)
        const dividendData = await getDividendData(form.symbol);
        if (dividendData && dividendData.length > 0) {
          // 每筆以「發放日(退回除息日)」為基準排序,取 ≤ 今天的新一筆 —
          // 避免帶入已公告但尚未發放(未來日期)的股利
          const today = toLocalDateString();
          const candidates = dividendData
            .map(record => ({ record, dividendDate: record.paymentDate || record.exDividendDate }))
            .filter(x => x.dividendDate)
            .sort((a, b) => (a.dividendDate > b.dividendDate ? 1 : a.dividendDate < b.dividendDate ? -1 : 0)); // 升冪
          const paid = candidates.filter(x => x.dividendDate <= today);
          // 已發放 → 最新一筆;全部未發放(新股/從未配息)→ 最接近的未來一筆供參考
          const selected = paid.length > 0 ? paid[paid.length - 1] : candidates[0];

          if (selected && selected.record.cashDividend > 0) {
            const latestDividend = selected.record;
            // 保留公告的完整精度(台股每股現金股利可達小數 8 位,如台積電 3.49978969),
            // 僅以 toFixed(8) 清除加總產生的浮點尾差,不可進位捨去 —
            // 總額 = 每股 × 股數後由 DecimalHelper 捨去到整數元,先捨入會造成紀錄失真
            const dividendPerShare = Number(latestDividend.cashDividend.toFixed(8));
            const isPaid = paid.length > 0;
            // 已發放 → 帶入發放日;未發放 → 不覆寫日期(避免未來日期入帳,由使用者自行填寫)
            // 注意:dividendService 對缺漏 CashDividendPaymentDate 的記錄會退回除息日/資料日,
            // 極少數情況可能將「已除息未發放」誤判為已發放,屬資料源限制
            setForm(prev => ({
              ...prev,
              price: String(dividendPerShare),
              ...(isPaid ? { date: selected.dividendDate } : {})
            }));
            // 以本地時區解析 yyyy-MM-dd,避免 new Date('YYYY-MM-DD') 走 UTC 造成負時區顯示提前一天
            const formatDate = (s: string) => {
              const [y, m, d] = s.split('-').map(Number);
              return new Date(y, m - 1, d).toLocaleDateString('zh-TW');
            };
            const dateInfo = `每股 $${dividendPerShare}，除息日：${formatDate(latestDividend.exDividendDate)}，發放日：${formatDate(selected.dividendDate)}`;
            if (isPaid) {
              showSuccess(`已自動填入持股數、最新股利金額與日期（${dateInfo}）`);
            } else {
              showWarning(`尚無已發放股利，已帶入最接近的公告資料供參考（${dateInfo}，尚未發放）；交易日期未自動填入，請自行確認`);
            }
          }
        }
      } catch (error) {
        console.error('獲取股利/持股資料失敗:', error);
        // 靜默失敗，不影響正常操作
      }
    }
  };

  const validateForm = (): string | null => {
    // 資金調整的驗證邏輯
    if (['cash_deposit', 'cash_withdrawal'].includes(form.type)) {
      if (!form.adjustmentAmount || parseFloat(form.adjustmentAmount) <= 0) {
        return '請輸入有效的調整金額';
      }
      
      if (form.type === 'cash_withdrawal' && state.currentAccount) {
        const withdrawalAmount = parseFloat(form.adjustmentAmount);
        if (withdrawalAmount > state.currentAccount.balance) {
          return '餘額不足，無法提取此金額';
        }
      }
      
      return null;
    }
    
    // 一般交易的驗證邏輯
    if (!form.symbol.trim()) return '請輸入股票代號';
    // FIN-06:parseQuantity 對 NaN / 科學記號 / 小數股數皆正確處理
    if (parseQuantity(form.quantity) === null) return '請輸入有效的股數(正整數)';

    // 價格驗證(共用規則:股票異動可填 0/留空,一般交易須 > 0)
    const priceError = validateTransactionPrice(form.price, form.type);
    if (priceError) return priceError;
    
    // 使用包含稅費的實際成本來驗證餘額
    if (form.type === 'buy' && state.currentAccount) {
      const transactionDetails = calculateTransactionDetails();
      const totalWithFees = Math.abs(transactionDetails.actualCost);
      
      if (totalWithFees > state.currentAccount.balance) {
        return '餘額不足，無法完成買入交易（含稅費）';
      }
    }
    
    return null;
  };

  const handleSubmit = async () => {
    // FIN-07:防重送 — 送出期間不得重複觸發
    if (submitting) return;

    const error = validateForm();
    if (error) {
      showError(error);
      return;
    }

    // 已通過 validateForm,此處必為有效值(股利允許至小數 8 位,其餘 2 位)
    const quantity = parseQuantity(form.quantity) ?? 0;
    const price = parsePrice(form.price, maxPriceDecimals(form.type)) ?? 0;
    
    // 計算包含稅費的真實成本
    const transactionDetails = calculateTransactionDetails();
    const totalWithFees = Math.abs(transactionDetails.actualCost); // 使用包含稅費的實際成本

    // 檢查股票是否存在（可選的驗證）
    const stockExists = state.stocks.some(stock => 
      stock.symbol.toUpperCase() === form.symbol.toUpperCase()
    );

    if (!stockExists) {
      showWarning(`股票代號 ${form.symbol} 不存在於系統中，但交易將正常記錄`);
    }

    try {
      setSubmitting(true);

      // 處理資金調整
      if (['cash_deposit', 'cash_withdrawal'].includes(form.type)) {
        const adjustmentAmount = parseFloat(form.adjustmentAmount!);
        
        await addTransaction({
          symbol: 'CASH', // 使用特殊符號表示現金調整
          stockName: '現金調整', // 現金調整的股票名稱
          type: form.type as 'cash_deposit' | 'cash_withdrawal',
          quantity: 1, // 固定為1
          price: adjustmentAmount, // 金額統一為正數
          total: adjustmentAmount, // 總額統一為正數
          fees: 0,
          taxes: 0, // 資金調整無稅金
          transactionDate: new Date(form.date), // 傳遞用戶選擇的交易日期
          notes: `${form.type === 'cash_deposit' ? '存入' : '提取'}資金 $${adjustmentAmount.toLocaleString()} - 手動調整`
        });
      } else {
        // 一般股票交易
        let transactionNote = '';
        switch (form.type) {
          case 'buy':
            transactionNote = '買入 - 透過手動交易新增';
            break;
          case 'sell':
            transactionNote = '賣出 - 透過手動交易新增';
            break;
          case 'dividend':
            transactionNote = '股利 - 透過手動交易新增';
            break;
          case 'stock_increase':
            transactionNote = '股票增加 - 透過手動交易新增';
            break;
          case 'stock_decrease':
            transactionNote = '股票減少 - 透過手動交易新增';
            break;
          default:
            transactionNote = '交易 - 透過手動交易新增';
        }
        
        // 所有數量都記錄為正數，在計算時根據交易類型判斷增減
        let adjustedQuantity = quantity; // 保持正數記錄
        
        // 使用統一的股票名稱管理器
        const stockName = StockNameManager.getStockName(form.symbol.toUpperCase());
        
        await addTransaction({
          symbol: form.symbol.toUpperCase(),
          stockName: stockName, // 添加股票名稱
          type: form.type,
          quantity: adjustedQuantity,
          price: price,
          total: ['stock_increase', 'stock_decrease'].includes(form.type) ? 0 : totalWithFees, // 股票異動總成本為0
          fees: ['stock_increase', 'stock_decrease'].includes(form.type) ? 0 : transactionDetails.brokerageFee,
          taxes: ['stock_increase', 'stock_decrease'].includes(form.type) ? 0 : transactionDetails.transactionTax,
          adjustmentAmount: form.transactionAdjustmentAmount ? parseFloat(form.transactionAdjustmentAmount) : undefined,
          adjustmentReason: form.transactionAdjustmentReason || undefined,
          transactionDate: new Date(form.date), // 傳遞用戶選擇的交易日期
          notes: transactionNote
        });
      }
      

      const getTypeText = (type: string) => {
        switch (type) {
          case 'buy': return '買入';
          case 'sell': return '賣出';
          case 'dividend': return '股利';
          case 'stock_increase': return '股票增加';
          case 'stock_decrease': return '股票減少';
          case 'cash_deposit': return '存入資金';
          case 'cash_withdrawal': return '提取資金';
          default: return '交易';
        }
      };

      const successMessage = ['cash_deposit', 'cash_withdrawal'].includes(form.type) 
        ? `${getTypeText(form.type)} $${parseFloat(form.adjustmentAmount!).toLocaleString()} 記錄成功！`
        : `${getTypeText(form.type)} ${form.symbol.toUpperCase()} ${quantity} 股記錄成功！`;

      showSuccess(successMessage);

      // 清空表單，但股數維持 1000
      const now = new Date();
      setForm({
        symbol: '',
        type: 'buy',
        quantity: '1000', // 維持預設股數 1000
        price: '',
        date: toLocalDateString(now), // UI-06:本地時區日期
        adjustmentAmount: '',
        transactionAdjustmentAmount: '',
        transactionAdjustmentReason: '',
      });

      // 清空股票資訊狀態
      setStockInfo({ found: false });

      // Note: Notification system automatically handles closing notifications
    } catch (error) {
      logger.component.error('新增交易失敗', error);
      showError(`新增交易失敗：${error instanceof Error ? error.message : '未知錯誤'}`);
    } finally {
      setSubmitting(false);
    }
  };


  // 計算交易的各項費用和實際成本
  const calculateTransactionDetails = () => {
    const quantity = parseQuantity(form.quantity) ?? 0;
    const price = parseFloat(form.price) || 0;
    const tradeAmount = DecimalHelper.calculateTradeAmount(quantity, price);
    
    // 手續費計算
    let brokerageFee: number;
    if (form.type === 'dividend') {
      brokerageFee = 10; // 股利匯費固定 10 元
    } else if (['stock_increase', 'stock_decrease', 'cash_deposit', 'cash_withdrawal'].includes(form.type)) {
      brokerageFee = 0;
    } else {
      brokerageFee = DecimalHelper.calculateBrokerageFee(tradeAmount);
    }
    
    // 交易稅或二代健保(證交稅率依代號+交易日期判別:債券ETF停徵/ETF 0.1%/股票 0.3%)
    let transactionTax: number;
    if (form.type === 'sell') {
      transactionTax = DecimalHelper.calculateTransactionTax(
        tradeAmount,
        DecimalHelper.getTransactionTaxRate(form.symbol, form.date ? new Date(form.date) : undefined),
      );
    } else if (form.type === 'dividend') {
      transactionTax = DecimalHelper.calculateSupplementaryPremium(tradeAmount);
    } else {
      transactionTax = 0;
    }
    
    // 實際成本或收入計算
    let actualCost: number;
    if (form.type === 'buy') {
      actualCost = tradeAmount + brokerageFee;
    } else if (form.type === 'sell') {
      actualCost = tradeAmount - brokerageFee - transactionTax;
    } else if (form.type === 'dividend') {
      actualCost = tradeAmount - brokerageFee - transactionTax;
    } else if (form.type === 'cash_deposit') {
      actualCost = tradeAmount;
    } else if (form.type === 'cash_withdrawal') {
      actualCost = -tradeAmount;
    } else {
      actualCost = 0;
    }
    
    return {
      subtotal: tradeAmount,
      brokerageFee,
      transactionTax,
      actualCost
    };
  };

  // CSV 範例模板

  // 驗證批次交易記錄
  const validateBatchTransaction = (row: any, lineNumber: number): BatchTransaction => {
    const errors: string[] = [];
    
    // 日期驗證
    const date = row['日期'] || row['date'] || '';
    if (!date) {
      errors.push('日期不能為空');
    } else if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      errors.push('日期格式錯誤，應為 YYYY-MM-DD');
    }

    // 股票代號驗證
    const symbol = row['股票代號'] || row['symbol'] || '';
    if (!symbol) {
      errors.push('股票代號不能為空');
    }

    // 股票名稱（可選）
    const stockName = row['股票名稱'] || row['stockName'] || '';

    // 交易類型驗證
    const type = row['交易類型'] || row['type'] || '';
    const validTypes = ['buy', 'sell', 'dividend', 'cash_deposit', 'cash_withdrawal', 'stock_increase', 'stock_decrease'];
    if (!type || !validTypes.includes(type)) {
      errors.push('交易類型錯誤，應為 buy/sell/dividend/cash_deposit/cash_withdrawal/stock_increase/stock_decrease');
    }

    // 股數驗證
    const quantity = parseFloat(row['股數'] || row['quantity'] || '0');
    if (isNaN(quantity) || quantity <= 0) {
      errors.push('股數必須為正數');
    }

    // 價格驗證：股票異動允許0價格
    const price = parseFloat(row['價格'] || row['price'] || '0');
    if (isNaN(price) || price < 0) {
      errors.push('價格必須為數字且不能為負數');
    }
    
    // 股票異動外的其他交易類型需要大於0的價格
    if (!['stock_increase', 'stock_decrease'].includes(type) && price <= 0) {
      errors.push('價格必須為正數（股票異動除外）');
    }

    // 手續費驗證（可選）
    const fees = row['手續費'] || row['fees'];
    const parsedFees = fees ? parseFloat(fees) : 0;

    // 交易稅驗證（可選）
    const taxes = row['交易稅'] || row['taxes'] || row['tax'];
    const parsedTaxes = taxes ? parseFloat(taxes) : 0;
    
    // 調整總額驗證（可選）
    const adjustmentAmount = row['調整總額'] || row['adjustmentAmount'] || '';
    const parsedAdjustmentAmount = adjustmentAmount ? parseFloat(adjustmentAmount) : undefined;
    
    // 調整原因（可選）
    const adjustmentReason = row['調整原因'] || row['adjustmentReason'] || '';

    // 備註（可選）
    const notes = row['備註'] || row['notes'] || '';

    return {
      symbol,
      stockName: stockName || undefined, // 添加 stockName 欄位
      type: type as 'buy' | 'sell' | 'dividend' | 'cash_deposit' | 'cash_withdrawal' | 'stock_increase' | 'stock_decrease',
      quantity,
      price,
      date,
      fees: parsedFees,
      taxes: parsedTaxes,
      adjustmentAmount: parsedAdjustmentAmount,
      adjustmentReason: adjustmentReason || undefined,
      notes,
      status: 'pending',
      isValid: errors.length === 0,
      errors
    };
  };

  // 優化的 CSV 解析功能
  const parseCSV = (text: string): BatchTransaction[] => {
    // FIN-09(完整):以 parseCsvText 解析 — 支援引號內含換行的多行欄位
    // (試算表匯出的備註常含換行,舊版先 split('\n') 會讓這些列欄位數錯位而被丟棄)
    const rows = parseCsvText(text.trim());
    if (rows.length < 2) {
      throw new Error('CSV 文件必須包含標題行和至少一行資料');
    }

    // 預處理標題並建立映射表以提升效能
    const rawHeaders = rows[0].map(h => h.trim());
    const headerMap = new Map<string, number>();
    rawHeaders.forEach((header, index) => {
      headerMap.set(header, index);
    });

    // 預分配陣列容量以減少記憶體重分配
    const transactions: BatchTransaction[] = new Array(rows.length - 1);
    let validTransactionCount = 0;

    for (let i = 1; i < rows.length; i++) {
      const values = rows[i];
      if (values.length !== rawHeaders.length) {
        continue; // 跳過格式錯誤的行
      }

      // 使用 Map 查找以提高效能
      const row = Object.create(null); // 無原型物件，減少記憶體開銷
      for (let j = 0; j < values.length; j++) {
        row[rawHeaders[j]] = values[j].trim();
      }

      const transaction = validateBatchTransaction(row, i + 1);
      transactions[validTransactionCount++] = transaction;
    }

    // 調整陣列大小以釋放未使用的記憶體
    transactions.length = validTransactionCount;

    return transactions;
  };

  // 處理文件上傳
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      showError('請上傳 CSV 格式的檔案');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const transactions = parseCSV(text);
        setBatchTransactions(transactions);
        setImportResult(null);
        
        const validCount = transactions.filter(t => t.isValid).length;
        const invalidCount = transactions.length - validCount;
        
        if (invalidCount > 0) {
          showWarning(`成功解析 ${transactions.length} 筆資料，其中 ${validCount} 筆有效，${invalidCount} 筆有錯誤`);
        } else {
          showSuccess(`成功解析 ${transactions.length} 筆資料，其中 ${validCount} 筆有效，${invalidCount} 筆有錯誤`);
        }
      } catch (error: unknown) {
        showError(`解析檔案失敗：${error instanceof Error ? error.message : '未知錯誤'}`);
      }
    };
    reader.readAsText(file);
  };

  // 批次匯入進度狀態
  const [importProgress, setImportProgress] = React.useState<{
    current: number;
    total: number;
    currentItem: string;
    phase: 'validation' | 'import' | 'calculation' | 'complete';
  } | null>(null);

  // 處理批次交易資料轉換
  const prepareBatchTransactions = (validTransactions: BatchTransaction[]) => {
    return validTransactions.map(transaction => {
      let total: number;
      let transactionTax = 0;
      let defaultFees = 0;
      let appliedFees = 0;
      let tradeAmount = 0;
      
      if (transaction.type === 'cash_deposit') {
        tradeAmount = DecimalHelper.calculateTradeAmount(transaction.quantity, transaction.price);
        total = tradeAmount;
        appliedFees = transaction.fees ?? 0;
      } else if (transaction.type === 'cash_withdrawal') {
        tradeAmount = DecimalHelper.calculateTradeAmount(transaction.quantity, transaction.price);
        total = -tradeAmount;
        appliedFees = transaction.fees ?? 0;
      } else if (['stock_increase', 'stock_decrease'].includes(transaction.type)) {
        total = 0;
        transactionTax = 0;
        appliedFees = 0;
      } else {
        tradeAmount = DecimalHelper.calculateTradeAmount(transaction.quantity, transaction.price);
        
        if (transaction.type === 'dividend') {
          defaultFees = 10;
          transactionTax = DecimalHelper.calculateSupplementaryPremium(tradeAmount);
        } else if (transaction.type === 'sell') {
          defaultFees = DecimalHelper.calculateBrokerageFee(tradeAmount);
          transactionTax = DecimalHelper.calculateTransactionTax(
            tradeAmount,
            DecimalHelper.getTransactionTaxRate(transaction.symbol, transaction.date ? new Date(transaction.date) : undefined),
          );
        } else if (transaction.type === 'buy') {
          defaultFees = DecimalHelper.calculateBrokerageFee(tradeAmount);
          transactionTax = 0;
        } else {
          defaultFees = DecimalHelper.calculateBrokerageFee(tradeAmount);
          transactionTax = 0;
        }
        
        appliedFees = typeof transaction.fees === 'number' ? transaction.fees : defaultFees;
        if (transaction.type === 'buy') {
          total = tradeAmount + appliedFees;
        } else if (transaction.type === 'sell') {
          total = tradeAmount - appliedFees - transactionTax;
        } else {
          total = tradeAmount - appliedFees - transactionTax;
        }
      }

      // 所有數量都記錄為正數，在計算時根據交易類型判斷增減
      let adjustedQuantity = transaction.quantity; // 保持正數記錄
      
      // 優先使用 CSV 中的股票名稱，如果沒有則使用統一的股票名稱管理器
      const stockName = transaction.symbol === 'CASH' 
        ? '現金調整'
        : (transaction.stockName || StockNameManager.getStockName(transaction.symbol));
      
      return {
        symbol: transaction.symbol === 'CASH' ? 'CASH' : transaction.symbol.toUpperCase(),
        stockName: stockName,
        type: transaction.type as 'buy' | 'sell' | 'dividend' | 'cash_deposit' | 'cash_withdrawal' | 'stock_increase' | 'stock_decrease',
        quantity: adjustedQuantity,
        price: Math.abs(transaction.price), // 確保金額為正數
        total: Math.abs(total), // 確保總額為正數
        fees: typeof transaction.fees === 'number' ? transaction.fees : appliedFees,
        taxes: transaction.taxes !== undefined ? transaction.taxes : transactionTax,
        adjustmentAmount: transaction.adjustmentAmount,
        adjustmentReason: transaction.adjustmentReason,
        transactionDate: new Date(transaction.date),
        notes: transaction.notes || '批次匯入'
      };
    });
  };

  // 執行批次匯入 - 使用優化版本
  const handleBatchImport = async () => {
    const validTransactions = batchTransactions.filter(t => t.isValid);
    if (validTransactions.length === 0) {
      showWarning('沒有有效的交易記錄可以匯入');
      return;
    }

    setBatchProcessing(true);
    setImportProgress(null);

    try {
      // 準備交易資料
      const preparedTransactions = prepareBatchTransactions(validTransactions);
      
      // 確定目標帳戶 ID
      const targetUserId = state.currentAccountId || state.authUser?.uid;
      
      if (!targetUserId) {
        throw new Error('無法取得帳戶資訊，請重新登入');
      }
      
      // 使用優化的批次匯入
      const result = await TransactionService.optimizedBatchImport(
        targetUserId,
        preparedTransactions,
        (progress) => {
          setImportProgress(progress);
          
          // 更新 UI 中的交易狀態
          if (progress.phase === 'import') {
            const processed = Math.min(progress.current, validTransactions.length);
            setBatchTransactions(prev => 
              prev.map((t, idx) => {
                if (!t.isValid) return t;
                
                const validIdx = prev.slice(0, idx).filter(pt => pt.isValid).length;
                if (validIdx < processed) {
                  return { ...t, status: 'success' as const };
                } else if (validIdx === processed) {
                  return { ...t, status: 'pending' as const };
                }
                return t;
              })
            );
          }
        }
      );

      setImportResult(result);
      
      // 最終更新所有交易狀態
      setBatchTransactions(prev => 
        prev.map(t => {
          if (!t.isValid) return t;
          return { ...t, status: 'success' as const };
        })
      );
      
      if (result.success > 0) {
        const message = `成功匯入 ${result.success} 筆交易記錄${result.failed > 0 ? `，${result.failed} 筆失敗` : ''}`;
        if (result.failed > 0) {
          showWarning(message);
        } else {
          showSuccess(message);
        }
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : '未知錯誤';
      showError(`批次匯入失敗：${errorMessage}`);
      setImportResult({
        success: 0,
        failed: validTransactions.length,
        errors: [errorMessage]
      });
    } finally {
      setBatchProcessing(false);
      setImportProgress(null);
    }
  };

  // 下載 CSV 範例
  const downloadTemplate = () => {
    const blob = new Blob([csvTemplate], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'transaction_template.csv';
    link.click();
  };

  // 清除批次資料
  const clearBatchData = () => {
    setBatchTransactions([]);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };


  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <PageHeader
        title="手動交易"
        icon={Add}
        subtitle={`管理您的股票交易記錄 • ${state.currentAccount ? `可用餘額 $${state.currentAccount.balance.toLocaleString()}` : '無帳戶資訊'}`}
        actions={null}
      />

      {/* 交易帳戶餘額顯示 */}
      {state.currentAccount && (
        <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AccountBalance color="primary" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {state.currentAccount.name} - 可用餘額
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    ${state.currentAccount.balance.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
              
              {/* 🔧 臨時修復工具已移除:餘額重算已收斂至 balanceCalculation 單一實作,
                  由 transactionService.updateAccountBalanceAfterBatch 提供 */}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* 交易表單 */}
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Tab 切換器 */}
        <Tabs 
          value={currentTab} 
          onChange={(_, newValue) => setCurrentTab(newValue)}
          sx={{ 
            mb: 3,
            '& .MuiTab-root': {
              fontSize: '1rem',
              fontWeight: 'bold',
              textTransform: 'none',
              '&.Mui-selected': {
                color: '#1565c0',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#1565c0',
              height: 3,
            },
          }}
        >
          <Tab 
            icon={<Edit />} 
            label="手動輸入" 
            iconPosition="start" 
            sx={{ gap: 1 }} 
          />
          <Tab 
            icon={<CloudUpload />} 
            label="批次匯入" 
            iconPosition="start" 
            sx={{ gap: 1 }} 
          />
        </Tabs>

        <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
          {currentTab === 0 ? '新增交易記錄' : '批次匯入交易'}
        </Typography>

        {currentTab === 0 ? (
          /* 手動輸入表單 */
          <Box sx={{ 
            display: 'grid', 
            gap: 3, 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } 
          }}>
          {/* 股票代號 (僅非資金調整時顯示) */}
          {!['cash_deposit', 'cash_withdrawal'].includes(form.type) && (
            <TextField
              fullWidth
              label="股票代號"
              variant="outlined"
              value={form.symbol}
              onChange={(e) => handleInputChange('symbol', e.target.value.toUpperCase())}
              placeholder="例如：2330, 2317"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {stockSearching ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Search color={stockInfo.found ? "success" : "disabled"} />
                    )}
                  </InputAdornment>
                ),
              }}
              helperText={
                form.symbol.length >= 4 ? (
                  stockSearching ? (
                    "🔍 搜尋中..."
                  ) : stockInfo.found ? (
                    `✅ ${stockInfo.name} - 當前股價：$${stockInfo.currentPrice?.toFixed(2)}`
                  ) : (
                    // 使用統一的股票名稱管理器顯示名稱，如果有的話
                    (() => {
                      const stockName = StockNameManager.getStockName(form.symbol);
                      return stockName !== form.symbol 
                        ? `📝 ${stockName} - 無即時報價`
                        : "⚠️ 查無此股票代號";
                    })()
                  )
                ) : form.symbol.length > 0 ? (
                  "請輸入至少 4 個字元"
                ) : null
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#1565c0',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1565c0',
                  },
                },
                '& .MuiFormHelperText-root': {
                  color: form.symbol.length >= 4 ? (
                    stockSearching ? '#1976d2' : stockInfo.found ? '#2e7d32' : '#d32f2f'
                  ) : '#666',
                  fontWeight: 500,
                },
              }}
            />
          )}

          {/* 交易類型 */}
          <TextField
            fullWidth
            select
            label="交易類型"
            variant="outlined"
            value={form.type}
            onChange={(e) => handleInputChange('type', e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: '#1565c0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#1565c0',
                },
              },
            }}
          >
            <MenuItem value="buy">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShoppingCart sx={{ color: '#4caf50', fontSize: 20 }} />
                買入
              </Box>
            </MenuItem>
            <MenuItem value="sell">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Sell sx={{ color: '#f44336', fontSize: 20 }} />
                賣出
              </Box>
            </MenuItem>
            <MenuItem value="dividend">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MonetizationOn sx={{ color: '#ff9800', fontSize: 20 }} />
                股利
              </Box>
            </MenuItem>
            <MenuItem value="stock_increase">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Add sx={{ color: '#4caf50', fontSize: 20 }} />
                股票增加
              </Box>
            </MenuItem>
            <MenuItem value="stock_decrease">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Delete sx={{ color: '#f44336', fontSize: 20 }} />
                股票減少
              </Box>
            </MenuItem>
            <MenuItem value="cash_deposit">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountBalanceWallet sx={{ color: '#4caf50', fontSize: 20 }} />
                存入資金
              </Box>
            </MenuItem>
            <MenuItem value="cash_withdrawal">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountBalanceWallet sx={{ color: '#f44336', fontSize: 20 }} />
                提取資金
              </Box>
            </MenuItem>
          </TextField>

          {/* 股數 (僅非資金調整時顯示) */}
          {!['cash_deposit', 'cash_withdrawal'].includes(form.type) && (
            <TextField
              fullWidth
              label="股數"
              variant="outlined"
              type="number"
              value={form.quantity}
              onChange={(e) => handleInputChange('quantity', e.target.value)}
              placeholder="輸入股數"
              inputProps={{ min: 1, step: 1 }}
              helperText={form.type === 'stock_decrease' ? '股票減少數量記錄為正數，系統自動計算' : undefined}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#1565c0',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1565c0',
                  },
                },
              }}
            />
          )}

          {/* 價格 (僅非資金調整時顯示) */}
          {!['cash_deposit', 'cash_withdrawal'].includes(form.type) && (
            <TextField
              fullWidth
              label="價格"
              variant="outlined"
              type="number"
              value={form.price}
              onChange={(e) => handleInputChange('price', e.target.value)}
              placeholder="輸入成交價格"
              inputProps={form.type === 'dividend'
                ? { min: 0, step: 'any' } // 每股股利公告可達小數 8 位
                : { min: 0.01, step: 0.01 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#1565c0',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1565c0',
                  },
                },
              }}
            />
          )}

          {/* 資金調整金額 (僅當選擇現金存入/提取時顯示) */}
          {['cash_deposit', 'cash_withdrawal'].includes(form.type) && (
            <TextField
              fullWidth
              label="調整金額"
              variant="outlined"
              type="number"
              value={form.adjustmentAmount}
              onChange={(e) => handleInputChange('adjustmentAmount', e.target.value)}
              placeholder="輸入調整金額"
              inputProps={{ min: 0.01, step: 0.01 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              helperText={form.type === 'cash_deposit' ? '存入資金到帳戶' : '從帳戶提取資金'}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#1565c0',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1565c0',
                  },
                },
                '& .MuiFormHelperText-root': {
                  color: form.type === 'cash_deposit' ? '#4caf50' : '#f44336',
                  fontWeight: 500,
                },
              }}
            />
          )}

          {/* 交易日期 */}
          <TextField
            fullWidth
            label="交易日期"
            variant="outlined"
            type="date"
            value={form.date}
            onChange={(e) => handleInputChange('date', e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: '#1565c0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#1565c0',
                },
              },
            }}
          />
          
          {/* 交易調整總額 */}
          <TextField
            fullWidth
            label="調整總額"
            variant="outlined"
            type="number"
            value={form.transactionAdjustmentAmount}
            onChange={(e) => handleInputChange('transactionAdjustmentAmount', e.target.value)}
            placeholder="可選：對單筆交易的調整金額"
            inputProps={{ step: 0.01 }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            helperText="正值表示增加，負值表示減少，留空表示無調整"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 152, 0, 0.04)',
                '&:hover fieldset': {
                  borderColor: '#ff9800',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#ff9800',
                },
              },
            }}
          />
          
          {/* 交易調整原因 */}
          <TextField
            fullWidth
            label="調整原因"
            variant="outlined"
            value={form.transactionAdjustmentReason}
            onChange={(e) => handleInputChange('transactionAdjustmentReason', e.target.value)}
            placeholder="調整金額的原因說明（可選）"
            disabled={!form.transactionAdjustmentAmount || parseFloat(form.transactionAdjustmentAmount) === 0}
            helperText="在有調整金額時建議填寫調整原因"
            sx={{ 
              gridColumn: '1 / -1',
              '& .MuiOutlinedInput-root': {
                backgroundColor: !form.transactionAdjustmentAmount || parseFloat(form.transactionAdjustmentAmount) === 0 
                  ? 'rgba(0, 0, 0, 0.02)' 
                  : 'rgba(255, 152, 0, 0.04)',
                '&:hover fieldset': {
                  borderColor: !form.transactionAdjustmentAmount || parseFloat(form.transactionAdjustmentAmount) === 0 
                    ? '#1565c0' 
                    : '#ff9800',
                },
                '&.Mui-focused fieldset': {
                  borderColor: !form.transactionAdjustmentAmount || parseFloat(form.transactionAdjustmentAmount) === 0 
                    ? '#1565c0' 
                    : '#ff9800',
                },
              }
            }}
          />

        </Box>
        ) : (
          /* 批次匯入表單 */
          <BatchImportForm
            batchTransactions={batchTransactions}
            batchProcessing={batchProcessing}
            importProgress={importProgress}
            importResult={importResult}
            fileInputRef={fileInputRef}
            onFileUpload={handleFileUpload}
            onBatchImport={handleBatchImport}
            onDownloadTemplate={downloadTemplate}
            onClearBatchData={clearBatchData}
          />
        )}

        {/* 預覽新增交易資訊 (僅手動輸入時顯示) */}
        {currentTab === 0 && (['cash_deposit', 'cash_withdrawal'].includes(form.type) ? form.adjustmentAmount : (form.quantity && form.price)) && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ 
              p: 3, 
              bgcolor: form.type === 'buy' ? '#e8f5e8' : form.type === 'sell' ? '#ffebee' : ['cash_deposit', 'cash_withdrawal'].includes(form.type) ? '#f3e5f5' : '#fff8e1',
              borderRadius: 2,
              border: `2px solid ${form.type === 'buy' ? '#4caf50' : form.type === 'sell' ? '#f44336' : ['cash_deposit', 'cash_withdrawal'].includes(form.type) ? '#9c27b0' : '#ff9800'}`
            }}>
              <Typography variant="h6" fontWeight="bold" sx={{ textAlign: 'center', mb: 3, color: form.type === 'buy' ? '#2e7d32' : form.type === 'sell' ? '#c62828' : ['cash_deposit', 'cash_withdrawal'].includes(form.type) ? '#7b1fa2' : '#ef6c00' }}>
                📋 {['cash_deposit', 'cash_withdrawal'].includes(form.type) ? '資金調整預覽' : '交易紀錄預覽'}
              </Typography>
              
              {(() => {
                const details = calculateTransactionDetails();
                const adjustmentAmount = parseFloat(form.adjustmentAmount || '0');
                // 使用用戶選擇的日期（不顯示時間）
                const selectedDate = new Date(`${form.date}T00:00:00`);
                
                return (
                  <>
                    {/* 基本交易資訊 */}
                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, mb: 3 }}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">成交日期</Typography>
                        <Typography variant="subtitle1" fontWeight="bold">{selectedDate.toLocaleDateString('zh-TW')}</Typography>
                      </Box>
                      
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">交易類別</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 0.5 }}>
                          {form.type === 'buy' && <ShoppingCart sx={{ color: '#4caf50', fontSize: 18 }} />}
                          {form.type === 'sell' && <Sell sx={{ color: '#f44336', fontSize: 18 }} />}
                          {form.type === 'dividend' && <MonetizationOn sx={{ color: '#ff9800', fontSize: 18 }} />}
                          {form.type === 'cash_deposit' && <AccountBalanceWallet sx={{ color: '#4caf50', fontSize: 18 }} />}
                          {form.type === 'cash_withdrawal' && <AccountBalanceWallet sx={{ color: '#f44336', fontSize: 18 }} />}
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ color: form.type === 'buy' ? '#4caf50' : form.type === 'sell' ? '#f44336' : form.type === 'cash_deposit' ? '#4caf50' : form.type === 'cash_withdrawal' ? '#f44336' : '#ff9800' }}>
                            {form.type === 'buy' ? '買入' : form.type === 'sell' ? '賣出' : form.type === 'dividend' ? '股利' : form.type === 'cash_deposit' ? '存入資金' : form.type === 'cash_withdrawal' ? '提取資金' : '交易'}
                          </Typography>
                        </Box>
                      </Box>
                      
                      {!['cash_deposit', 'cash_withdrawal'].includes(form.type) ? (
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">股票名稱</Typography>
                          <Typography variant="h6" fontWeight="bold" color="primary">{form.symbol.toUpperCase()}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {StockNameManager.getStockName(form.symbol)}
                    </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">調整金額</Typography>
                          <Typography variant="h6" fontWeight="bold" color="primary">${adjustmentAmount.toLocaleString()}</Typography>
                          <Typography variant="caption" color="text.secondary">{form.type === 'cash_deposit' ? '存入到帳戶' : '從帳戶提取'}</Typography>
                        </Box>
                      )}
                      
                      {/* 股票相關資訊 (僅非資金調整時顯示) */}
                      {!['cash_deposit', 'cash_withdrawal'].includes(form.type) && (
                        <>
                          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">成交股數</Typography>
                            <Typography variant="h6" fontWeight="bold">{parseInt(form.quantity).toLocaleString()}</Typography>
                            <Typography variant="caption" color="text.secondary">股</Typography>
                          </Box>
                          
                          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">成交價</Typography>
                            <Typography variant="h6" fontWeight="bold">${parseFloat(form.price).toFixed(2)}</Typography>
                          </Box>
                          
                          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">成交價金</Typography>
                            <Typography variant="h6" fontWeight="bold">${details.subtotal.toLocaleString()}</Typography>
                          </Box>
                        </>
                      )}
                    </Box>
                    
                    {/* 費用計算 (僅非資金調整時顯示) */}
                    {!['cash_deposit', 'cash_withdrawal'].includes(form.type) && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, mb: 3 }}>
                          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">手續費</Typography>
                            <Typography variant="subtitle2" fontWeight="medium" color={details.brokerageFee > 0 ? 'error.main' : 'text.secondary'}>
                              {details.brokerageFee > 0 ? `$${details.brokerageFee.toLocaleString()}` : '-'}
                            </Typography>
                            {form.type === 'dividend' ? (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>匯費</Typography>
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>0.1425%</Typography>
                            )}
                          </Box>
                          
                          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">交易稅</Typography>
                            <Typography variant="subtitle2" fontWeight="medium" color={details.transactionTax > 0 ? 'error.main' : 'text.secondary'}>
                              {details.transactionTax > 0 ? `$${details.transactionTax.toLocaleString()}` : '-'}
                            </Typography>
                            {form.type === 'sell' ? (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>0.3%</Typography>
                            ) : form.type === 'dividend' ? (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>二代健保 2.11%</Typography>
                            ) : null}
                          </Box>
                          
                          <Box sx={{ textAlign: 'center', p: 2, bgcolor: form.type === 'buy' ? 'rgba(244, 67, 54, 0.1)' : 'rgba(76, 175, 80, 0.1)', borderRadius: 1, border: `1px solid ${form.type === 'buy' ? '#f44336' : '#4caf50'}` }}>
                            <Typography variant="caption" color="text.secondary">單次成本</Typography>
                            <Typography variant="h6" fontWeight="bold" color={form.type === 'buy' ? 'error.main' : 'success.main'}>
                              {form.type === 'buy' ? '-' : '+'}${Math.abs(details.actualCost).toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {form.type === 'buy' ? '支出' : form.type === 'sell' ? '收入' : '股利'}
                            </Typography>
                          </Box>
                        </Box>
                      </>
                    )}
                    
                    {/* 資金調整簡化預覽 */}
                    {['cash_deposit', 'cash_withdrawal'].includes(form.type) && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ textAlign: 'center', p: 3, bgcolor: form.type === 'cash_deposit' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)', borderRadius: 1, border: `2px solid ${form.type === 'cash_deposit' ? '#4caf50' : '#f44336'}` }}>
                          <Typography variant="h5" fontWeight="bold" color={form.type === 'cash_deposit' ? 'success.main' : 'error.main'}>
                            {form.type === 'cash_deposit' ? '+' : '-'}${adjustmentAmount.toLocaleString()}
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 1 }}>
                            {form.type === 'cash_deposit' ? '💰 存入到帳戶' : '💸 從帳戶提取'}
                          </Typography>
                        </Box>
                      </>
                    )}
                    
                    {/* 餘額檢查和提示 */}
                    {form.type === 'buy' && state.currentAccount && details.actualCost > state.currentAccount.balance && (
                      <Box sx={{ 
                        mt: 2, 
                        p: 2, 
                        bgcolor: 'rgba(244, 67, 54, 0.1)', 
                        borderRadius: 1,
                        border: '1px solid #f44336'
                      }}>
                        <Typography variant="body2" color="error" sx={{ textAlign: 'center', fontWeight: 'bold', mb: 1 }}>
                          ⚠️ 餘額不足，無法完成交易
                        </Typography>
                        <Typography variant="caption" color="error" sx={{ textAlign: 'center', display: 'block' }}>
                          需要資金：${details.actualCost.toLocaleString()} | 當前餘額：${state.currentAccount.balance.toLocaleString()}
                        </Typography>
                      </Box>
                    )}
                    
                    {form.type !== 'buy' && (
                      <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          {form.type === 'sell' ? '💡 賣出後資金將增加到您的帳戶' : '💡 股利收入將記錄到交易歷史'}
                        </Typography>
                      </Box>
                    )}
                  </>
                );
              })()}
            </Box>
          </>
        )}

        {/* 提交按鈕 (僅手動輸入時顯示) */}
        {currentTab === 0 && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button
              variant="contained"
              color={form.type === 'buy' ? 'success' : form.type === 'sell' ? 'error' : 'warning'}
              startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <Add />}
              onClick={handleSubmit}
              disabled={submitting}
              size="large"
              sx={{ 
                px: 4, 
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                ...(form.type === 'dividend' && {
                  backgroundColor: '#ff9800',
                  '&:hover': {
                    backgroundColor: '#f57c00',
                  }
                })
              }}
            >
              新增{form.type === 'buy' ? '買入' : form.type === 'sell' ? '賣出' : form.type === 'dividend' ? '股利' : form.type === 'cash_deposit' ? '存入資金' : form.type === 'cash_withdrawal' ? '提取資金' : '交易'}記錄
            </Button>
        </Box>
        )}
      </Paper>

    </Box>
  );
};

export default ManualTrading;
